// Hover hints for [data-toggle="tooltip"] and .with-tooltip: text from the title attribute, above the element
// or to the right with data-placement="right", flipped when it doesn't fit. Delegated on document, so content
// loaded later (card stats, dashboard rows) needs no init
const SELECTOR = '[data-toggle="tooltip"], .with-tooltip';
const GAP = 6; // between the element and the bubble, the arrow sits in it
const EDGE = 8; // min distance to the viewport edge
const ARROW_INSET = 10; // min distance from the arrow's center to the bubble's side, clear of the rounded corner

let tip = null;
let owner = null;

export function initTooltips() {
    // Mouse only: on a tap the hint would stick until the next tap, and iOS may swallow the click
    document.addEventListener('pointerover', function (e) {
        if (e.pointerType === 'touch') return;

        const el = e.target.closest(SELECTOR);
        if (el === owner) return;

        hideTooltip();
        if (el) show(el);
    });
    // Leaving the window
    document.addEventListener('mouseout', function (e) {
        if (!e.relatedTarget) hideTooltip();
    });
    // A click often replaces the element (dashboard rows) or opens the modal over it
    document.addEventListener('pointerdown', hideTooltip, true);
    window.addEventListener('scroll', hideTooltip, {capture: true, passive: true});
}

function hideTooltip() {
    tip?.remove();
    owner?.removeAttribute('aria-describedby');
    tip = owner = null;
}

function show(el) {
    // The title moves to data-tip, so the browser's own tooltip doesn't show on top; an icon-only element keeps it
    // as its accessible name
    // getAttribute: an <svg> has no title property
    if (el.hasAttribute('title')) {
        el.dataset.tip = el.getAttribute('title');
        if (!el.textContent.trim() && !el.hasAttribute('aria-label') && el.tagName !== 'IMG') {
            el.setAttribute('aria-label', el.dataset.tip);
        }
        el.removeAttribute('title');
    }
    if (!el.dataset.tip) return;

    owner = el;
    tip = document.createElement('div');
    tip.className = 'pf-tooltip';
    tip.id = 'pf-tooltip';
    tip.role = 'tooltip';
    tip.textContent = el.dataset.tip;
    // A popover goes to the top layer, so it shows above an open modal dialog too; without popover support
    // (iOS < 17) it's a plain fixed element
    tip.popover = 'manual';
    document.body.append(tip);
    tip.showPopover?.();
    el.setAttribute('aria-describedby', tip.id);

    place(el.getBoundingClientRect(), el.dataset.placement === 'right' ? 'right' : 'top');
    requestAnimationFrame(() => tip?.classList.add('is-in'));
}

function place(target, side) {
    const {width, height} = tip.getBoundingClientRect();
    const viewport = document.documentElement.clientWidth;
    let left;
    let top;

    if (side === 'right') {
        left = target.right + GAP;
        if (left + width > viewport - EDGE) {
            side = 'left';
            left = target.left - GAP - width;
        }
        top = target.top + target.height / 2 - height / 2;
    } else {
        top = target.top - GAP - height;
        if (top < EDGE) {
            side = 'bottom';
            top = target.bottom + GAP;
        }
        left = Math.min(Math.max(target.left + target.width / 2 - width / 2, EDGE), viewport - EDGE - width);
        // The arrow keeps pointing at the element when the bubble is pushed off the edge, but stays on the bubble
        const arrow = Math.min(Math.max(target.left + target.width / 2 - left, ARROW_INSET), width - ARROW_INSET);
        tip.style.setProperty('--arrow-x', arrow + 'px');
    }

    tip.dataset.side = side;
    tip.style.left = left + 'px';
    tip.style.top = top + 'px';
}
